/**
 * context/app/useEngineerActions.js - engineer CRUD,
 * a user's role, and an owner email to a display name.
 * Moved verbatim out of AppProvider (AppContext.jsx), which calls it at the
 * same point (same hook order) with the state it reads.
 */
import { useCallback } from 'react';
import { toSnake } from './rows';
import { API_BASE } from './apiClient';

export function useEngineerActions({ refreshQuietly, fetchAll, users }) {
  // -- ENGINEERS ------------------------------------------------
  const addEngineer = useCallback(
    async (d) => {
      try {
        await fetch(`${API_BASE}/api/engineers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toSnake(d)),
        });
        await refreshQuietly(['engineers']);
      } catch (err) {
        console.error('addEngineer error:', err);
      }
    },
    [refreshQuietly]
  );

  const updateEngineer = useCallback(
    async (id, updates) => {
      try {
        const res = await fetch(`${API_BASE}/api/engineers/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toSnake(updates)),
        });
        if (res.ok) {
          await refreshQuietly(['engineers']);
        } else if (res.status === 404) {
          const payload = { ...updates, id };
          await fetch(`${API_BASE}/api/engineers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(toSnake(payload)),
          });
          await refreshQuietly(['engineers']);
        }
      } catch (err) {
        console.error('updateEngineer error:', err);
      }
    },
    [refreshQuietly]
  );

  const deleteEngineer = useCallback(
    async (id) => {
      try {
        await fetch(`${API_BASE}/api/engineers/${id}`, { method: 'DELETE' });
        await refreshQuietly(['engineers']);
      } catch (err) {
        console.error('deleteEngineer error:', err);
      }
    },
    [refreshQuietly]
  );

  /* Change a user's role in the registry (managers of access only - the
     server refuses anyone else). True when it was saved. */
  const updateUserRole = useCallback(
    async (email, role) => {
      try {
        const res = await fetch(`${API_BASE}/api/users/${encodeURIComponent(email)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        });
        await fetchAll();
        return res.ok;
      } catch (err) {
        console.error('updateUserRole error:', err);
        return false;
      }
    },
    [fetchAll]
  );

  /* Resolve an owner email → display name via the users registry.
     Falls back to a prettified email local-part ("j.doe@x.com" → "J Doe")
     so unknown/legacy owners still render readably. */
  const resolveOwnerName = useCallback(
    (email) => {
      if (!email) return '';
      const e = String(email).trim().toLowerCase();
      const hit = users.find((u) => (u.email || '').toLowerCase() === e);
      if (hit && hit.name) return hit.name;
      const local = e.split('@')[0] || e;
      return (
        local
          .split(/[._-]+/)
          .filter(Boolean)
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
          .join(' ') || e
      );
    },
    [users]
  );
  return { resolveOwnerName, updateUserRole, addEngineer, updateEngineer, deleteEngineer };
}
